import dayjs from "dayjs";
import styles from "./styles.module.scss";
import { default as InsightIcon } from "@/assets/icons/insight.svg?react";
import { BarGraph, TimeFrameSelectorDropdown } from "../../components/graph";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  getGovernanceRoleDetails,
  getGovernanceRolesCostGraph,
  getGovernanceUserDetails,
  getGovernanceUsersCostGraph,
} from "../../helpers/apis";
import { RelativeComponentLoader } from "../../components/loader";
import { Alert, AlertDescription, Stack } from "@altimateai/lego";

const COLORS_MAP = {
  cost: "#5B9FFF",
  // storage: "#FFCE73",
  // serverless: "#9382D9",
};

const GET_USER_GRAPH = "GET-USER-GRAPH";
const GET_USER_DETAILS = "GET-USER-DETAILS";
const GET_ROLE_GRAPH = "GET-ROLE-GRAPH";
const GET_ROLE_DETAILS = "GET-ROLE-DETAILS";

const TIME_FRAMES = [
  { label: "Day", value: 1 },
  { label: "Week", value: 7 },
  { label: "Month", value: 30 },
];

interface UserDetailsModalProps {
  user_name: string;
}

const UserDetailsModal = ({ user_name }: UserDetailsModalProps) => {
  const [period, setPeriod] = useState<number>(30);
  const {
    data: graphData,
    isLoading: isGraphLoading,
    isError: isGraphError,
  } = useQuery({
    queryKey: [GET_USER_GRAPH, period],
    queryFn: () => getGovernanceUsersCostGraph({ period, user_name }),
  });
  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useQuery({
    queryKey: [GET_USER_DETAILS],
    queryFn: () => getGovernanceUserDetails(user_name),
  });

  if (isGraphLoading || isUserLoading)
    return <RelativeComponentLoader label={"Loading user details..."} />;

  if (isUserError || isGraphError || !user || !graphData) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load user details. Please try again later.</AlertDescription>
      </Alert>
    );
  }

  return (
    <Stack direction="column" gap="sm" className="p-3 al-h-full" aria-label="User details modal">
      <Stack direction="row" gap="sm" align="center" className="mb-2">
        <div className="fs-4">Username: </div>
        <div className="text-primary fs-4">{user.user_name}</div>
        <div className="spacer" />
        <div className="text-muted">
          {dayjs().subtract(1, "month").format("DD MMM YY")}
          {" - "}
          {dayjs().format("DD MMM YY")}
        </div>
        <div className={styles.duration}>Last 1 Month</div>
      </Stack>
      <Stack direction="row" gap="xl" align="center" className="mb-2">
        <Stack direction="row" gap="xs">
          <span className="fw-semibold">Queries:</span>
          <span>{user.num_of_queries}</span>
        </Stack>
        <Stack direction="row" gap="xs">
          <span className="fw-semibold">Last Logged In:</span>
          <span className="text-muted">
            {user.last_success_login && dayjs(user.last_success_login).format("D MMM YYYY")}
          </span>
        </Stack>
      </Stack>
      <div className={styles.graph_container} aria-label="User cost graph">
        <div className={styles.timeframe_inside_graph}>
          <TimeFrameSelectorDropdown
            options={TIME_FRAMES}
            period={period}
            setPeriod={v => setPeriod(Number(v))}
          />
        </div>
        <BarGraph
          graphData={graphData.items}
          colorsMap={COLORS_MAP}
          yLabel="Cost"
          dataKeys={["cost"]}
        />
      </div>
      {user.insights && user.insights.length > 0 && (
        <>
          <div className="text-black fs-5">Insights</div>
          <div className={styles.insight_container}>
            {user.insights.map(tag => (
              <div className={styles.insight_card} key={tag.label}>
                <div>
                  <Stack direction="row" gap="sm" className="mb-2">
                    <InsightIcon />
                    <div className="fs-5">{tag.label}</div>
                  </Stack>
                  <div className="text-muted">{tag?.reason}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Stack>
  );
};

interface RoleDetailsModalProps {
  role_name: string;
}

const RoleDetailsModal = ({ role_name }: RoleDetailsModalProps) => {
  const [period, setPeriod] = useState<number>(30);
  const {
    data: graphData,
    isLoading: isGraphLoading,
    isError: isGraphError,
  } = useQuery({
    queryKey: [GET_ROLE_GRAPH, period],
    queryFn: () => getGovernanceRolesCostGraph({ period, role_name }),
  });
  const {
    data: role,
    isLoading: isRoleLoading,
    isError: isRoleError,
  } = useQuery({
    queryKey: [GET_ROLE_DETAILS],
    queryFn: () => getGovernanceRoleDetails(role_name),
  });

  if (isGraphLoading || isRoleLoading)
    return <RelativeComponentLoader label={"Loading role details..."} />;

  if (isRoleError || isGraphError || !role || !graphData) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load role details. Please try again later.</AlertDescription>
      </Alert>
    );
  }

  return (
    <Stack direction="column" gap="sm" className="p-3 al-h-full" aria-label="Role details modal">
      <Stack direction="row" gap="sm" align="center" className="mb-2">
        <div className="fs-4">Role: </div>
        <div className="text-primary fs-4">{role.role_name}</div>
        <div className="spacer" />
        <div className="text-muted">
          {dayjs().subtract(1, "month").format("DD MMM YY")}
          {" - "}
          {dayjs().format("DD MMM YY")}
        </div>
        <div className={styles.duration}>Last 1 Month</div>
      </Stack>
      <Stack direction="row" gap="xl" align="center" className="mb-2">
        <Stack direction="row" gap="xs">
          <span className="fw-semibold">Queries:</span>
          <span>{role.num_of_queries}</span>
        </Stack>
      </Stack>
      <div className={styles.graph_container} aria-label="Roles cost graph">
        <div className={styles.timeframe_inside_graph}>
          <TimeFrameSelectorDropdown
            options={TIME_FRAMES}
            period={period}
            setPeriod={v => setPeriod(Number(v))}
          />
        </div>
        <BarGraph
          graphData={graphData.items}
          colorsMap={COLORS_MAP}
          yLabel="Cost"
          dataKeys={["cost"]}
        />
      </div>
      {role.insights && role.insights.length > 0 && (
        <>
          <div className="text-black fs-5">Insights</div>
          <div className={styles.insight_container}>
            {role.insights.map(tag => (
              <div className={styles.insight_card} key={tag.label}>
                <div>
                  <Stack direction="row" gap="sm" className="mb-2">
                    <InsightIcon />
                    <div className="fs-5">{tag.label}</div>
                  </Stack>
                  <div className="text-muted">{tag?.reason}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Stack>
  );
};

export { UserDetailsModal, RoleDetailsModal };
